import {
  type EpocusEncounter,
  type EpocusEncounterQueryConstructor,
  RXDB_EPOCUS_ENCOUNTER_COLLECTION_NAME,
} from "$global-providers/rxdb-provider/collections/epocus-encounter";
import { useUser } from "$global-providers/user-provider/provider";
import { useMemo } from "react";
import { useRxData } from "rxdb-hooks";
import { normaliseProbeSerial } from "../../_lib/use-probe-associations";

export type TriageEntry = EpocusEncounter;

export type ToDocumentEntry = {
  encounter: EpocusEncounter;
  claimable: boolean;
};

const undocumentedQuery: EpocusEncounterQueryConstructor = (collection) =>
  collection?.find({
    selector: { status: { $in: ["imported", "draft"] } },
    sort: [{ date: "desc" }],
    limit: 100,
  });

// Disabled variant for roles without claimTriageEncounter — undefined keeps the
// query from firing, so unclaimed scans are never held in memory for them.
const disabledQuery: EpocusEncounterQueryConstructor = () => undefined;

const NO_ENTRIES: ToDocumentEntry[] = [];

export const useToDocumentQueue = ({
  enabled = true,
  probeSerials,
  allProbeSerials,
  restrictToLinkedSerials,
}: {
  enabled?: boolean;
  probeSerials: ReadonlyArray<string>;
  /**
   * Every registered serial, not just `probeSerials`. Required rather than
   * defaulted: a caller that passed only its own serials would silently treat
   * every colleague's scan as unmatched and surface it to the wrong clinician.
   */
  allProbeSerials: ReadonlyArray<string>;
  restrictToLinkedSerials: boolean;
}) => {
  const user = useUser();
  const { result: encounters, isFetching } = useRxData<EpocusEncounter>(
    RXDB_EPOCUS_ENCOUNTER_COLLECTION_NAME,
    enabled ? undocumentedQuery : disabledQuery,
  );

  const myRegisteredSerials = useMemo(
    () => new Set(probeSerials.map(normaliseProbeSerial).filter(Boolean)),
    [probeSerials],
  );

  const allRegisteredSerials = useMemo(
    () => new Set(allProbeSerials.map(normaliseProbeSerial).filter(Boolean)),
    [allProbeSerials],
  );

  const entries = useMemo<ToDocumentEntry[]>(() => {
    if (!enabled) return NO_ENTRIES;

    return encounters.flatMap<ToDocumentEntry>((encounter) => {
      if (encounter.performerId === user.id) {
        return [{ encounter, claimable: false }];
      }
      if (encounter.performerId !== null) return [];
      if (encounter.status !== "imported") return [];

      // The serial is stored verbatim on both sides, so both are normalised
      // before comparing — otherwise a stray space hides the scan forever.
      const serial = encounter.deviceSerialNumber
        ? normaliseProbeSerial(encounter.deviceSerialNumber)
        : "";

      if (myRegisteredSerials.has(serial)) {
        return [{ encounter, claimable: true }];
      }
      if (restrictToLinkedSerials) return [];

      // A scan no registered probe accounts for was never matched to anyone, so
      // it stays in the shared pool for whoever documents it first. A scan from
      // a colleague's probe is theirs to claim and is left out.
      if (!serial || !allRegisteredSerials.has(serial)) {
        return [{ encounter, claimable: true }];
      }

      return [];
    });
  }, [
    enabled,
    encounters,
    user.id,
    myRegisteredSerials,
    allRegisteredSerials,
    restrictToLinkedSerials,
  ]);

  // rxdb-hooks never settles isFetching when the query constructor returns
  // undefined, so report the disabled queue as loaded rather than perpetually
  // loading.
  if (!enabled) return { entries: NO_ENTRIES, loading: false };

  return { entries, loading: isFetching };
};
